import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  CreditCard,
  KeyRound,
  Plus,
  Save,
  Settings,
  Trash2,
  WalletCards,
  PackageCheck,
  Copy,
} from 'lucide-react';

import { useLoja } from './LojaContext';
import { Button } from '../../components/ui/button';
import LojaAdminCatalog from './LojaAdminCatalog';

const emptyPix = () => ({
  id: crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`,
  label: 'PIX',
  key_type: 'phone',
  key: '',
  holder: '',
  city: 'BRASILIA',
  active: true,
});

export default function LojaAdmin() {
  const {
    user,
    token,
    API,
    authHeaders,
  } = useLoja();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [copiedField, setCopiedField] = useState('');

  const copyText = async (value, fieldId = '') => {
    try {
      await navigator.clipboard.writeText(
        String(value ?? '')
      );

      setCopiedField(fieldId);

      setTimeout(() => {
        setCopiedField('');
      }, 1500);
    } catch (err) {
      setError(
        'Não foi possível copiar para a área de transferência.'
      );
    }
  };

  const copyProductData = async (item, orderId) => {
    const fields = item.custom_fields || [];

    const text = fields
      .map(field =>
        `${field.label}: ${field.value ?? ''}`
      )
      .join('\n');

    await copyText(
      text,
      `${orderId}-${item.product_id}-all`
    );
  };

  const [settings, setSettings] = useState({
    manual_pix_enabled: false,
    manual_pix_keys: [],
    mercadopago_enabled: false,
    mercadopago_pix_enabled: false,
    mercadopago_card_enabled: false,
    mercadopago_configured: false,
    mercadopago_access_token_masked: null,
    mercadopago_webhook_configured: false,
    mercadopago_webhook_secret_masked: null,
  });

  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderAction, setOrderAction] = useState('');

  useEffect(() => {
    if (!token || !user?.is_admin) {
      setLoading(false);
      return;
    }

    loadSettings();
    loadOrders();
  }, [token, user]);

  const loadOrders = async () => {
    setLoadingOrders(true);

    try {
      const response = await axios.get(
        `${API}/admin/orders`,
        {
          headers: authHeaders(),
        }
      );

      setOrders(response.data || []);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível carregar os pedidos.'
      );
    } finally {
      setLoadingOrders(false);
    }
  };

  const markOrderPaid = async orderId => {
    const confirmed = window.confirm(
      'Confirma que o PIX deste pedido foi recebido?'
    );

    if (!confirmed) return;

    setOrderAction(orderId);
    setMessage('');
    setError('');

    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/mark-paid`,
        {},
        {
          headers: authHeaders(),
        }
      );

      setMessage('Pagamento PIX confirmado.');
      await loadOrders();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível confirmar o pagamento.'
      );
    } finally {
      setOrderAction('');
    }
  };

  const deliverOrder = async orderId => {
    const confirmed = window.confirm(
      'Confirma que este pedido foi entregue?'
    );

    if (!confirmed) return;

    setOrderAction(orderId);
    setMessage('');
    setError('');

    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/deliver`,
        {},
        {
          headers: authHeaders(),
        }
      );

      setMessage('Pedido marcado como entregue.');
      await loadOrders();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível atualizar o pedido.'
      );
    } finally {
      setOrderAction('');
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(
        `${API}/admin/payment-settings`,
        {
          headers: authHeaders(),
        }
      );

      setSettings(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível carregar as configurações.'
      );
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const updatePix = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      manual_pix_keys: prev.manual_pix_keys.map(
        (item, i) =>
          i === index
            ? { ...item, [field]: value }
            : item
      ),
    }));
  };

  const addPix = () => {
    setSettings(prev => ({
      ...prev,
      manual_pix_keys: [
        ...(prev.manual_pix_keys || []),
        emptyPix(),
      ],
    }));
  };

  const removePix = index => {
    setSettings(prev => ({
      ...prev,
      manual_pix_keys: prev.manual_pix_keys.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const payload = {
        manual_pix_enabled:
          !!settings.manual_pix_enabled,

        manual_pix_keys:
          settings.manual_pix_keys || [],

        mercadopago_enabled:
          !!settings.mercadopago_enabled,

        mercadopago_pix_enabled:
          !!settings.mercadopago_pix_enabled,

        mercadopago_card_enabled:
          !!settings.mercadopago_card_enabled,

        // Vazio significa manter o valor atual.
        mercadopago_access_token:
          accessToken.trim() || null,

        mercadopago_webhook_secret:
          webhookSecret.trim() || null,
      };

      const response = await axios.put(
        `${API}/admin/payment-settings`,
        payload,
        {
          headers: authHeaders(),
        }
      );

      setSettings(response.data);
      setAccessToken('');
      setWebhookSecret('');

      setMessage(
        'Configurações salvas com sucesso.'
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível salvar as configurações.'
      );
    } finally {
      setSaving(false);
    }
  };

  const removeMercadoPagoCredentials = async () => {
    const confirmed = window.confirm(
      'Remover Access Token e Webhook Secret do Mercado Pago?'
    );

    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axios.delete(
        `${API}/admin/payment-settings/mercadopago-token`,
        {
          headers: authHeaders(),
        }
      );

      setAccessToken('');
      setWebhookSecret('');

      await loadSettings();

      setMessage(
        'Credenciais do Mercado Pago removidas.'
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Não foi possível remover as credenciais.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <Navigate
        to="/loja/login"
        replace
      />
    );
  }

  if (!loading && !user?.is_admin) {
    return (
      <Navigate
        to="/loja"
        replace
      />
    );
  }

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-400">
        Carregando painel administrativo...
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div>
        <div className="flex items-center gap-2 text-fuchsia-400 text-sm font-semibold mb-1">
          <Settings className="w-4 h-4" />
          Administração
        </div>

        <h1 className="text-2xl font-black">
          Loja MARKIMAGEM
        </h1>

        <p className="text-sm text-slate-400 mt-1">
          Configure os meios de pagamento disponíveis
          para seus clientes.
        </p>
      </div>

      {message && (
        <div className="border border-emerald-700 bg-emerald-950/40 text-emerald-300 rounded-xl p-4 text-sm">
          {message}
        </div>
      )}

      {error && (
        <div className="border border-red-800 bg-red-950/40 text-red-300 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {/* PEDIDOS */}
      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <PackageCheck className="w-5 h-5 text-emerald-400" />
              Pedidos
            </div>

            <p className="text-xs text-slate-400 mt-1">
              Acompanhe pagamentos e entregas da loja.
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={loadOrders}
            disabled={loadingOrders}
            className="border-slate-700"
          >
            {loadingOrders ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </div>

        {loadingOrders && orders.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">
            Carregando pedidos...
          </div>
        ) : orders.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            Nenhum pedido encontrado.
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const payment = order.payment || {};
              const isManualPix =
                payment.provider === 'manual';

              const waitingManual =
                isManualPix &&
                order.status !== 'paid' &&
                order.status !== 'delivered';

              const canDeliver =
                order.status === 'paid';

              return (
                <div
                  key={order.id}
                  className="border border-slate-700 rounded-xl p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">

                    <div>
                      <div className="font-semibold text-sm">
                        Pedido #{String(order.id).slice(0, 8)}
                      </div>

                      <div className="text-xs text-slate-400 mt-1">
                        {order.user_email || 'Cliente'}
                      </div>

                      <div className="text-xs text-slate-500 mt-1">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleString('pt-BR')
                          : ''}
                      </div>
                    </div>

                    <div className="md:text-right">
                      <div className="font-black text-fuchsia-400">
                        {Number(order.total || 0).toLocaleString(
                          'pt-BR',
                          {
                            style: 'currency',
                            currency: 'BRL',
                          }
                        )}
                      </div>

                      <OrderStatus status={order.status} />
                    </div>

                  </div>

                  {Array.isArray(order.items) &&
                    order.items.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-800 space-y-4">
                      {order.items.map((item, itemIndex) => {
                        const customFields =
                          item.custom_fields || [];

                        const allCopyId =
                          `${order.id}-${item.product_id}-all`;

                        return (
                          <div
                            key={
                              `${item.product_id}-${itemIndex}`
                            }
                            className="rounded-xl border border-slate-800 bg-slate-950/60 p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-semibold text-sm text-slate-100">
                                  {item.product_name}
                                </div>

                                <div className="text-xs text-slate-500 mt-1">
                                  Quantidade: {item.quantity}
                                </div>
                              </div>

                              <div className="text-xs text-slate-400">
                                {Number(
                                  item.subtotal || 0
                                ).toLocaleString(
                                  'pt-BR',
                                  {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }
                                )}
                              </div>
                            </div>

                            {customFields.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-semibold text-fuchsia-300">
                                    Informações do produto
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      copyProductData(
                                        item,
                                        order.id
                                      )
                                    }
                                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-white"
                                  >
                                    <Copy className="w-3.5 h-3.5" />

                                    {copiedField ===
                                    allCopyId
                                      ? 'Copiado!'
                                      : 'Copiar todos'}
                                  </button>
                                </div>

                                {customFields.map(
                                  (field, fieldIndex) => {
                                    const copyId =
                                      `${order.id}-${item.product_id}-${field.id}-${fieldIndex}`;

                                    return (
                                      <button
                                        key={copyId}
                                        type="button"
                                        onClick={() =>
                                          copyText(
                                            field.value,
                                            copyId
                                          )
                                        }
                                        className="w-full flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2 text-left hover:border-fuchsia-700 transition-colors"
                                        title="Toque para copiar"
                                      >
                                        <div className="min-w-0">
                                          <div className="text-[11px] uppercase tracking-wide text-slate-500">
                                            {field.label}
                                          </div>

                                          <div className="text-sm text-slate-100 break-all mt-0.5">
                                            {String(
                                              field.value ??
                                              ''
                                            )}
                                          </div>
                                        </div>

                                        <div className="shrink-0 flex items-center gap-1 text-xs text-fuchsia-300">
                                          <Copy className="w-4 h-4" />

                                          {copiedField ===
                                          copyId
                                            ? 'Copiado!'
                                            : 'Copiar'}
                                        </div>
                                      </button>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-800 text-xs text-slate-400">
                    Pagamento:{' '}
                    <span className="text-slate-200">
                      {payment.method === 'pix' &&
                      payment.provider === 'mercadopago'
                        ? 'PIX Mercado Pago'
                        : payment.provider === 'manual'
                          ? 'PIX Direto'
                          : order.payment_method === 'mercadopago_card'
                            ? 'Cartão Mercado Pago'
                            : order.payment_method || 'Não informado'}
                    </span>
                  </div>

                  {(waitingManual || canDeliver) && (
                    <div className="flex flex-wrap gap-2 mt-4">

                      {waitingManual && (
                        <Button
                          type="button"
                          onClick={() => markOrderPaid(order.id)}
                          disabled={orderAction === order.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircleIcon />
                          Confirmar pagamento PIX
                        </Button>
                      )}

                      {canDeliver && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => deliverOrder(order.id)}
                          disabled={orderAction === order.id}
                          className="border-slate-700"
                        >
                          <PackageCheck className="w-4 h-4 mr-2" />
                          Marcar como entregue
                        </Button>
                      )}

                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}

      </section>

      {/* PIX MANUAL */}
      <LojaAdminCatalog />

      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-5">

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <KeyRound className="w-5 h-5 text-fuchsia-400" />
              PIX Direto
            </div>

            <p className="text-xs text-slate-400 mt-1">
              Receba diretamente em uma ou mais chaves PIX.
            </p>
          </div>

          <Toggle
            checked={settings.manual_pix_enabled}
            onChange={value =>
              updateField(
                'manual_pix_enabled',
                value
              )
            }
          />
        </div>

        <div className="space-y-4">
          {(settings.manual_pix_keys || []).map(
            (pix, index) => (
              <div
                key={pix.id || index}
                className="border border-slate-700 rounded-xl p-4 space-y-3"
              >
                <div className="grid md:grid-cols-2 gap-3">

                  <Field label="Nome da chave">
                    <input
                      value={pix.label || ''}
                      onChange={e =>
                        updatePix(
                          index,
                          'label',
                          e.target.value
                        )
                      }
                      className={inputClass}
                      placeholder="Ex.: PIX Principal"
                    />
                  </Field>

                  <Field label="Tipo">
                    <select
                      value={pix.key_type || 'phone'}
                      onChange={e =>
                        updatePix(
                          index,
                          'key_type',
                          e.target.value
                        )
                      }
                      className={inputClass}
                    >
                      <option value="cpf">CPF</option>
                      <option value="cnpj">CNPJ</option>
                      <option value="phone">Telefone</option>
                      <option value="email">E-mail</option>
                      <option value="random">
                        Chave aleatória
                      </option>
                    </select>
                  </Field>

                  <Field label="Chave PIX">
                    <input
                      value={pix.key || ''}
                      onChange={e =>
                        updatePix(
                          index,
                          'key',
                          e.target.value
                        )
                      }
                      className={inputClass}
                      placeholder="Digite a chave"
                    />
                  </Field>

                  <Field label="Titular">
                    <input
                      value={pix.holder || ''}
                      onChange={e =>
                        updatePix(
                          index,
                          'holder',
                          e.target.value
                        )
                      }
                      className={inputClass}
                      placeholder="Nome do titular"
                    />
                  </Field>

                  <Field label="Cidade">
                    <input
                      value={pix.city || ''}
                      onChange={e =>
                        updatePix(
                          index,
                          'city',
                          e.target.value
                        )
                      }
                      className={inputClass}
                      placeholder="BRASILIA"
                    />
                  </Field>

                  <div className="flex items-end justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-300 pb-3">
                      <input
                        type="checkbox"
                        checked={pix.active !== false}
                        onChange={e =>
                          updatePix(
                            index,
                            'active',
                            e.target.checked
                          )
                        }
                      />
                      Chave ativa
                    </label>

                    <button
                      type="button"
                      onClick={() => removePix(index)}
                      className="mb-1 p-2.5 text-red-400 hover:bg-red-950/50 rounded-lg"
                      title="Excluir chave"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                </div>
              </div>
            )
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addPix}
          className="border-slate-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar chave PIX
        </Button>
      </section>

      {/* MERCADO PAGO */}
      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-5">

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <WalletCards className="w-5 h-5 text-sky-400" />
              Mercado Pago
            </div>

            <p className="text-xs text-slate-400 mt-1">
              Pagamentos com confirmação automática.
            </p>
          </div>

          <Toggle
            checked={settings.mercadopago_enabled}
            onChange={value =>
              updateField(
                'mercadopago_enabled',
                value
              )
            }
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">

          <PaymentOption
            title="PIX Mercado Pago"
            description="QR Code e confirmação automática."
            checked={
              settings.mercadopago_pix_enabled
            }
            onChange={value =>
              updateField(
                'mercadopago_pix_enabled',
                value
              )
            }
          />

          <PaymentOption
            title="Cartão de crédito"
            description="Checkout seguro hospedado pelo Mercado Pago."
            icon={<CreditCard className="w-5 h-5" />}
            checked={
              settings.mercadopago_card_enabled
            }
            onChange={value =>
              updateField(
                'mercadopago_card_enabled',
                value
              )
            }
          />

        </div>

        <div className="grid md:grid-cols-2 gap-4">

          <Field label="Access Token">
            <input
              type="password"
              value={accessToken}
              onChange={e =>
                setAccessToken(e.target.value)
              }
              className={inputClass}
              placeholder={
                settings.mercadopago_configured
                  ? settings.mercadopago_access_token_masked ||
                    'Token já configurado'
                  : 'Cole o Access Token'
              }
              autoComplete="new-password"
            />

            <p className="text-[11px] text-slate-500 mt-1">
              {settings.mercadopago_configured
                ? 'Token cadastrado. Deixe vazio para manter o atual.'
                : 'Nenhum token cadastrado.'}
            </p>
          </Field>

          <Field label="Webhook Secret">
            <input
              type="password"
              value={webhookSecret}
              onChange={e =>
                setWebhookSecret(e.target.value)
              }
              className={inputClass}
              placeholder={
                settings.mercadopago_webhook_configured
                  ? settings.mercadopago_webhook_secret_masked ||
                    'Secret já configurado'
                  : 'Cole o Webhook Secret'
              }
              autoComplete="new-password"
            />

            <p className="text-[11px] text-slate-500 mt-1">
              {settings.mercadopago_webhook_configured
                ? 'Secret cadastrado. Deixe vazio para manter o atual.'
                : 'Nenhum Webhook Secret cadastrado.'}
            </p>
          </Field>

        </div>

        {(settings.mercadopago_configured ||
          settings.mercadopago_webhook_configured) && (
          <button
            type="button"
            onClick={removeMercadoPagoCredentials}
            className="text-xs text-red-400 hover:text-red-300"
          >
            Remover credenciais do Mercado Pago
          </button>
        )}
      </section>

      <div className="flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white min-w-[180px]"
        >
          <Save className="w-4 h-4 mr-2" />

          {saving
            ? 'Salvando...'
            : 'Salvar configurações'}
        </Button>
      </div>

    </div>
  );
}

const inputClass =
  'w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-fuchsia-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked
          ? 'bg-fuchsia-500'
          : 'bg-slate-700'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
          checked
            ? 'left-7'
            : 'left-1'
        }`}
      />
    </button>
  );
}

function PaymentOption({
  title,
  description,
  checked,
  onChange,
  icon,
}) {
  return (
    <div className="border border-slate-700 rounded-xl p-4 flex items-center justify-between gap-3">
      <div>
        <div className="font-semibold text-sm flex items-center gap-2">
          {icon}
          {title}
        </div>

        <div className="text-xs text-slate-500 mt-1">
          {description}
        </div>
      </div>

      <Toggle
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
}

function CheckCircleIcon() {
  return (
    <span className="mr-2 font-bold">
      ✓
    </span>
  );
}

function OrderStatus({ status }) {
  const labels = {
    pending: 'Pendente',
    pending_payment: 'Aguardando pagamento',
    paid: 'Pago',
    delivered: 'Entregue',
    cancelled: 'Cancelado',
  };

  const classes = {
    paid:
      'bg-emerald-950 text-emerald-300 border-emerald-800',
    delivered:
      'bg-sky-950 text-sky-300 border-sky-800',
    cancelled:
      'bg-red-950 text-red-300 border-red-800',
    pending:
      'bg-amber-950 text-amber-300 border-amber-800',
    pending_payment:
      'bg-amber-950 text-amber-300 border-amber-800',
  };

  return (
    <span
      className={`inline-block mt-2 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${
        classes[status] ||
        'bg-slate-800 text-slate-300 border-slate-700'
      }`}
    >
      {labels[status] || status || 'Pendente'}
    </span>
  );
}
